import React, { useEffect, useState } from "react"
import StandardList from "../../../common/listing/list.component"
import { defineMessages, injectIntl } from "react-intl"
import { Intl } from "../../../state/helpers/IntlProps"
import { Row } from "../../../common/listing/ListingTypes"
import { useDispatch, useSelector } from "react-redux"
import { MatrixItemDto, MatrixItemDtoAttributes } from "../../../state/product/matrix/matrix-item/MatrixItem"
import Icon from "../../../common/icon/icon.component"
import { deleteMatrixItem, updateMatrixItem } from "../../../state/product/ProductActions"
import MatrixItemsRestoreSection from "./MatrixItemsRestoreSection"
import { getActiveMatrixItems } from "../../../state/product/ProductSelectors"
import { Badge } from "react-bootstrap"
import { MATRIX_ITEM_STATUS, MATRIX_ITEM_UPDATE_OPTION, PRICING_STRATEGY } from "../../../state/product/Types"
import CustomBracketModal from "../../form/custom-bracket/modal.component"
import MatrixItemCostPriceComponent from "../component/MatrixItemCostPriceComponent"
import { RouteComponentProps, withRouter } from "react-router"
import useSaveChangesBeforeNavigateModal from "../../../common/hooks/UseSaveChangesBeforeNavigateModal"
import clsx from "clsx"
import Tooltip from "../../../common/tooltip/Tooltip"
import { FormattedMessage } from "react-intl"

const messages = defineMessages({
    header_name: {
        id: "product.variant.component.variant_listing_section.header_name",
        defaultMessage: "Variant name"
    },
    header_cost: {
        id: "product.variant.component.variant_listing_section.header_cost",
        defaultMessage: "Cost"
    },
    header_price: {
        id: "product.variant.component.variant_listing_section.header_price",
        defaultMessage: "Price"
    },
    header_bracket_cost: {
        id: "product.variant.component.variant_listing_section.header_bracket_cost",
        defaultMessage: "Bracket Cost"
    },
    header_bracket_price: {
        id: "product.variant.component.variant_listing_section.header_bracket_price",
        defaultMessage: "Bracket Price"
    },
    edit_custom_brackets: {
        id: "product.variant.component.variant_listing_section.edit_custom_brackets",
        defaultMessage: "Edit custom brackets"
    },
    [MATRIX_ITEM_STATUS.NEW]: {
        id: "product.variant.component.variant_listing_section.new_status",
        defaultMessage: "New"
    },
    [MATRIX_ITEM_STATUS.UPDATED]: {
        id: "product.variant.component.variant_listing_section.updated_status",
        defaultMessage: "Updated"
    }

})

type Props = {
    intl: Intl
    parentId: string
    isFormDirty: boolean
    isMatrixDirty: boolean
    onSave: () => void
}

function MatrixItemsListingSection(props: Props & RouteComponentProps) {

    const matrixItems: MatrixItemDto[] = useSelector(getActiveMatrixItems)
    const dispatch = useDispatch()
    const [modal, setModal] = useState(<></>)
    const { modal: SaveChangesBeforeNavigateModal, toggleShow, hasConfirmed } = useSaveChangesBeforeNavigateModal()
    const [editButtonClickProductId, setEditButtonClickProductId] = useState<string>()

    const navigateToVariant = (productId: string) => {
        props.history.push(`/products/edit/${props.parentId}/variants/${productId}`)
    }

    const openSaveChangesBeforeNavigateModal = (productId: string) => {
        setEditButtonClickProductId(productId)
        toggleShow()
    }

    const onItemEditClick = (item) => {
        if (props.isFormDirty || props.isMatrixDirty) {
            openSaveChangesBeforeNavigateModal(item.attributes.productId)
        } else {
            navigateToVariant(item.attributes.productId)
        }
    }

    useEffect(() => {
        if (hasConfirmed && editButtonClickProductId) {
            props.onSave()
            navigateToVariant(editButtonClickProductId)
        }
    }, [hasConfirmed])


    const getTitle = (item: MatrixItemDtoAttributes) => {
        switch (item.status_) {
        case MATRIX_ITEM_STATUS.NEW:
            return <>
                {item.product.attributes.title}
                <Badge className={"ml-2"} variant="success">
                    {props.intl.formatMessage(messages[item.status_])}
                </Badge>
            </>
        case MATRIX_ITEM_STATUS.UPDATED:
            return <>
                {item.product.attributes.title}
                <Badge className={"ml-2"} variant="warning">
                    {props.intl.formatMessage(messages[item.status_])}
                </Badge>
            </>
        default:
            return item.product.attributes.title
        }
    }

    const onEditCustomBracketsClick = (item) => {
        setModal(
            <CustomBracketModal
                onClose={brackets => {
                    dispatch(updateMatrixItem({
                        updateType: MATRIX_ITEM_UPDATE_OPTION.PRICING_BRACKETS,
                        id: item.id,
                        value: brackets,
                    }))
                    setModal(<></>)
                }}
                // @ts-ignore todo to update when global state is split and simplified
                value={item.attributes.product.attributes.pricingBrackets.map(pb => pb.attributes || pb)}
                pricingStrategy={item.attributes.product.attributes.pricingStrategy}
                billingMethods={item.attributes.product.attributes.billingMethods}
                currency={item.attributes.product.attributes.currency}
            />
        )
    }

    const isPricingStrategyCustomBracketOrBulk = (item) => {
        return item && item.attributes.product.attributes.pricingStrategy === PRICING_STRATEGY.CUSTOM_BRACKET
            || item && item.attributes.product.attributes.pricingStrategy === PRICING_STRATEGY.BULK
    }

    const isNewProduct = (item) => {
        return !props.parentId || !item.attributes.productId
    }

    const getEditButtom = (item) => {
        return (
            <a
                className={clsx("btn", "btn-link", "float-right", "p-0", { "disabled": isNewProduct(item) })} href="#"
                onClick={e => {
                    e.preventDefault()
                    onItemEditClick(item)
                }}
            >
                <Icon value={"edit"} />
            </a>
        )
    }

    const headers: string[] = matrixItems.length > 0 && isPricingStrategyCustomBracketOrBulk(matrixItems[0]) ? [
        props.intl.formatMessage(messages["header_name"]),
        "",
        props.intl.formatMessage(messages["header_bracket_cost"]),
        props.intl.formatMessage(messages["header_bracket_price"]),
        "",
    ] : [
        props.intl.formatMessage(messages["header_name"]),
        "",
        props.intl.formatMessage(messages["header_cost"]),
        props.intl.formatMessage(messages["header_price"]),
        "",
    ]

    const rows: Row<MatrixItemDto>[] = matrixItems.map(item => {
        const data = [
            getTitle(item.attributes),
            <div key={0}>
                {isPricingStrategyCustomBracketOrBulk(item) &&
                    <a className="text-primary" href="#"
                        onClick={e => {
                            e.preventDefault()
                            onEditCustomBracketsClick(item)
                        }}>
                        {props.intl.formatMessage(messages["edit_custom_brackets"])}
                    </a>
                }
            </div>,
            <MatrixItemCostPriceComponent
                key={1}
                hideLabel={true}
                inputType={MATRIX_ITEM_UPDATE_OPTION.COST}
                item={item}
            />,
            <MatrixItemCostPriceComponent
                key={2}
                hideLabel={true}
                inputType={MATRIX_ITEM_UPDATE_OPTION.PRICE}
                item={item}
            />,
            <div key={3} className={"d-flex justify-content-end align-items-center"} style={{ "gap": "16px" }}>
                {isNewProduct(item) ?
                    <Tooltip
                        tooltipId='tooltip_variant_save_product'
                        message={<FormattedMessage
                            id={"src.product.variant.tooltip_save_product"}
                            defaultMessage={"To edit new variants, please save the product."} />}>
                        <div className={"d-inline-block"} style={{ cursor: "not-allowed" }}>
                            {getEditButtom(item)}
                        </div>
                    </Tooltip>

                    :
                    getEditButtom(item)
                }

                <a className="text-danger float-right" href="#"
                    onClick={e => {
                        e.preventDefault()
                        dispatch(deleteMatrixItem({
                            id: item.id
                        }
                        ))
                    }}
                >
                    <Icon value={"trash"} />
                </a>
            </div>
        ]

        return {
            rowId: item.attributes.productId,
            data: data,
            metadata: item
        }
    })

    const footer = <MatrixItemsRestoreSection />

    return (
        <>
            {modal}
            {SaveChangesBeforeNavigateModal}
            <StandardList
                className={"p-0 pt-2"}
                headers={headers}
                rows={rows}
                footer={footer}
            />
        </>
    )
}

export default injectIntl(withRouter(MatrixItemsListingSection))


// WEBPACK FOOTER //
// ./src/product/variant/section/MatrixItemsListingSection.tsx